import ofertas from "@/services/ofertas";
import { sFormateaConSigno } from "./labelHelpers";
import type { DatosAdicionales } from "./types";

const EMPTY: DatosAdicionales = {
  nombrelente: "",
  tratamiento: "",
  coloracion: "",
  fotocromatico: "",
  descripcionprisma: "",
};

// Cache opcional (scope = batch) para deduplicar llamadas SOAP a `ofertas.*`
// con los mismos parámetros. Guarda la propia promesa: dos PDFs que pidan el
// mismo código a la vez comparten la request en vuelo, no se dispara dos veces.
export type DatosCache = Map<string, Promise<any>>;

export const createDatosCache = (): DatosCache => new Map();

// Tupla [OD, OI] de los datos adicionales de un pedido — formato que `generarPDF`
// espera cuando se le pre-suministran los datos via `opts.prefetchedDatos`.
export type DatosOjos = [DatosAdicionales, DatosAdicionales];

// Atajo: lanza en paralelo `obtenerDatosAdicionales` para OD y OI. Útil para
// pre-disparar los SOAPs de un pedido sin tener que componer el `Promise.all`
// en el caller.
export function obtenerDatosAmbosOjos(
  pedido: any,
  token: string | null | undefined,
  cache?: DatosCache,
): Promise<DatosOjos> {
  return Promise.all([
    obtenerDatosAdicionales(pedido, true, token, cache),
    obtenerDatosAdicionales(pedido, false, token, cache),
  ]);
}

function memo<T>(cache: DatosCache | undefined, key: string, fn: () => Promise<T>): Promise<T> {
  if (!cache) return fn();
  const hit = cache.get(key) as Promise<T> | undefined;
  if (hit) return hit;
  const p = fn();
  cache.set(key, p);
  return p;
}

// Formatea ESF/CIL/EJE/ADI del lado out de un ojo aplicando signo, decimales,
// paréntesis para el cilindro y "º" para el eje (cuando hay cilindro).
//
// NOTA: el OD escribe a `ESF_CTR` (sin L) mientras que el OI escribe a `ESF_CTRL`.
// Es un bug pre-existente que se preserva intencionadamente al refactorizar
// (cambiarlo modificaría el output del PDF). La esfera del OD se imprime sin
// formato como consecuencia.
function formateaOjoOut(ojoOut: any, escribirEnESF_CTR_sinL: boolean): void {
  if (!ojoOut) return;

  if (escribirEnESF_CTR_sinL) {
    ojoOut.ESF_CTR = sFormateaConSigno(ojoOut.ESF_CTRL, true, 2);
  } else {
    ojoOut.ESF_CTRL = sFormateaConSigno(ojoOut.ESF_CTRL, true, 2);
  }
  ojoOut.CIL_CTRL = sFormateaConSigno(ojoOut.CIL_CTRL, true, 2);
  ojoOut.EJE_CTRL = sFormateaConSigno(ojoOut.EJE_CTRL, false, 1);

  if (ojoOut.CIL_CTRL && ojoOut.CIL_CTRL !== "") {
    ojoOut.CIL_CTRL = `(${ojoOut.CIL_CTRL})`;
    ojoOut.EJE_CTRL = `${ojoOut.EJE_CTRL}º`;
  }

  if (ojoOut.ADI_CTRL && ojoOut.ADI_CTRL !== "") {
    ojoOut.ADI_CTRL = sFormateaConSigno(ojoOut.ADI_CTRL, false, 2);
  }
}

export function formateaGraduacionResultante(pedido: any): void {
  try {
    formateaOjoOut(pedido?.ojoOutDerecho, true);   // OD: bug pre-existente (escribe a ESF_CTR)
    formateaOjoOut(pedido?.ojoOutIzquierdo, false); // OI: escribe a ESF_CTRL
  } catch {
    // Silencio intencional: si el formateo falla, se conservan los valores raw.
  }
}

// Resuelve las descripciones de lente, tratamientos, coloración, fotocromático
// y prisma para un ojo a partir de las llamadas SOAP a `ofertas`.
// El parámetro opcional `cache` deduplica llamadas con los mismos parámetros
// dentro de un batch (ver `generarPDFMultiple`).
export async function obtenerDatosAdicionales(
  pedido: any,
  ojo: boolean,
  token: string | null | undefined,
  cache?: DatosCache,
): Promise<DatosAdicionales> {
  if (!pedido || !pedido.oClaseBAPI) return EMPTY;

  const orgVenta = pedido?.oClaseBAPI?.CABOUT?.VKORG;
  const ojoData = ojo ? pedido?.oClaseBAPI?.ojoInDerecho : pedido?.oClaseBAPI?.ojoInIzquierdo;
  if (!ojoData) return EMPTY;

  const cliente = pedido?.oClaseBAPI?.CLIENTE;
  const lenteCod = ojoData?.JER_EDI_INET ?? "";
  const trat = (code: any, tipo: string, adic: string) =>
    memo(cache, `trat|${orgVenta}|${code ?? ""}|${tipo}|${adic}`, () =>
      ofertas.getDescripcionTratamiento(orgVenta, code, tipo, adic, token));
  const prisma = (code: any) =>
    memo(cache, `prisma|${orgVenta}|${code ?? ""}`, () =>
      ofertas.getDescripcionBasePrisma(orgVenta, code, "", token));

  const [
    lente,
    descTrat1,
    descTrat2,
    descColoracion,
    descColor,
    descTono,
    descFotocromatico,
    descBasePrisma1,
    descBasePrisma2,
  ] = await Promise.all([
    memo(cache, `lente|${orgVenta}|${cliente}|${lenteCod}`, () =>
      ofertas.getCaracteristicasLente(orgVenta, cliente, lenteCod, token)),
    trat(ojoData?.TRATAM1, "", ""),
    trat(ojoData?.TRATAM2, "TR", "/"),
    trat(ojoData?.COLORACION, "CO", ""),
    trat(ojoData?.COLOR, "CL", "/"),
    trat(ojoData?.TONO, "TN", "/"),
    trat(ojoData?.FOTOCROMICO, "FT", ""),
    prisma(ojoData?.BASE_PR1),
    prisma(ojoData?.BASE_PR2),
  ]);

  const prismaParts: string[] = [];
  if (ojoData?.DIOP_PR1) {
    prismaParts.push(ojoData.DIOP_PR1);
    if (ojoData?.GRAD_PR1) prismaParts.push(`(${ojoData.GRAD_PR1})`);
    if (descBasePrisma1) prismaParts.push(`(${descBasePrisma1})`);
  }
  if (ojoData?.DIOP_PR2) {
    prismaParts.push(" ");
    prismaParts.push(ojoData.DIOP_PR2);
    if (ojoData?.GRAD_PR2) prismaParts.push(`(${ojoData.GRAD_PR2})`);
    if (descBasePrisma2) prismaParts.push(`(${descBasePrisma2})`);
  }

  return {
    nombrelente: lente?.Nombre ?? "",
    tratamiento: [descTrat1, descTrat2].filter(Boolean).join(""),
    coloracion: [descColoracion, descColor, descTono].filter(Boolean).join("/"),
    fotocromatico: descFotocromatico ?? "",
    descripcionprisma: prismaParts.join(" "),
  };
}
